import { useGetTinyArtworks } from "@/api/artwork/artwork";
import { useGetAllAttachments, type Attachment } from "@/api/attachment";
import { useGetAllMultipleprintsCoreInfo } from "@/api/multipleprint/multipleprint";
import {
  closePuckDrawer,
  openPuckDrawer,
} from "@/components/cms-puck/fields/puck-drawer-store";
import { PuckRichTextEditor } from "@/components/cms-puck/fields/PuckRichTextEditor";
import type { SelectionItem, SelectionItemType } from "@/lib/cms-puck/blocks/selection";
import {
  attachmentThumb as mediaUtilsThumb,
  attachmentTitle as mediaUtilsTitle,
} from "@/lib/cms-puck/media-utils";
import { Button } from "@/components/ui/button";
import SearchBar from "@/components/ui/searchbar";
import { useAuth } from "@/context/auth/AuthContext";
import { getDefaultAttachment } from "@/lib/image";
import type { MultipleprintCoreInfo, TinyArtwork } from "@/lib/types";
import { GripVertical, Pencil, XIcon } from "lucide-react";
import { useMemo, useRef, useState, type DragEvent } from "react";
import { useCmsNew } from "@/lib/cms/cms-new-i18n";
import { cmsPhrase } from "@/lib/cms/cms-new-phrases";
import { cn } from "@/lib/utils";

/**
 * Custom field Puck pour le bloc `ArtworkSelection` : recherche & sélectionne des
 * **Œuvres**, des **Multiples (estampes)** ET des **Images libres** de la
 * médiathèque du CMS (parité ancien CMS `Selection.imageId` — image/vidéo/embed).
 * Stocke `items: { type, id, caption? }[]` (ordre conservé). `caption` = légende
 * custom TRADUISIBLE par item (affichée par la bascule « Champ personnalisé »),
 * saisie dans un drawer avec RichText — extraite vers ContentString au save.
 */

const KEY = (it: SelectionItem) => `${it.type}:${it.id}`;

function isEmptyCaptionHtml(html: string | undefined): boolean {
  if (!html?.trim()) return true;
  const text = html
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .trim();
  return !text;
}

/** Vignette + libellé d'un média de la bibliothèque (image/vidéo/embed). */
function attachmentThumb(att: Attachment | undefined): string | undefined {
  return mediaUtilsThumb(att);
}

function attachmentTitle(att: Attachment | undefined, id: string): string {
  return mediaUtilsTitle(att, id);
}

function artworkThumb(art: TinyArtwork | undefined): string | undefined {
  if (!art?.attachments?.length) return undefined;
  const att = getDefaultAttachment(art.attachments, "artwork");
  return att?.mediumPublicUrl ?? att?.publicUrl ?? att?.largePublicUrl;
}

function Thumb({
  url,
  alt,
  size = 96,
}: {
  url?: string;
  alt?: string;
  size?: number;
}) {
  if (!url) {
    return (
      <div
        className="flex items-center justify-center rounded bg-slate-100 text-[10px] text-slate-400"
        style={{ width: size, height: size }}
      >
        —
      </div>
    );
  }
  return (
    <img
      src={url}
      alt={alt ?? ""}
      width={size}
      height={size}
      style={{ width: size, height: size, objectFit: "cover" }}
      className="rounded bg-slate-100"
    />
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
        active
          ? "bg-white text-slate-900 shadow-sm"
          : "text-slate-500 hover:text-slate-700"
      }`}
    >
      {children}
    </button>
  );
}

/** Contenu du drawer (monté dans le HOST → state stable). Possède sa copie de
 * travail des items pour rester cohérent même si le champ remonte. */
function SelectionPickerBody({
  initialItems,
  onChange,
}: {
  initialItems: SelectionItem[];
  onChange: (value: SelectionItem[]) => void;
}) {
  const cmsNew = useCmsNew();
  const [items, setItems] = useState<SelectionItem[]>(initialItems);
  const selected = useMemo(() => new Set(items.map(KEY)), [items]);
  const selectionOrder = useMemo(() => {
    const order = new Map<string, number>();
    items.forEach((it, index) => order.set(KEY(it), index + 1));
    return order;
  }, [items]);
  const onToggle = (item: SelectionItem) => {
    const k = KEY(item);
    const next = selected.has(k)
      ? items.filter((i) => KEY(i) !== k)
      : [...items, item];
    setItems(next);
    onChange(next);
  };
  const [tab, setTab] = useState<SelectionItemType>("artwork");
  const [search, setSearch] = useState("");
  const trimmed = search.trim();

  const { instanceData } = useAuth();
  const cmsId = instanceData?.CMS?.id;

  const { data: artworks = [], isLoading: loadingArt } = useGetTinyArtworks({
    where: trimmed
      ? { oeuvresDescriptionTitre: { contains: trimmed, mode: "insensitive" } }
      : {},
    orderBy: { oeuvresDescriptionTitre: "asc" },
    take: 60,
  });
  const { data: mpPage, isLoading: loadingMp } =
    useGetAllMultipleprintsCoreInfo(
      { page: 0, pageSize: 60 },
      trimmed || undefined,
    );
  const multiples: MultipleprintCoreInfo[] = mpPage?.data ?? [];
  // Médiathèque du CMS (mêmes médias que le picker Média). Recherche côté client
  // (légende/nom) — la liste est bornée au CMS de l'instance.
  const { data: libraryMedia = [], isLoading: loadingMedia } =
    useGetAllAttachments({
      where: cmsId ? { docId: cmsId } : { docId: "__none__" },
      order: { order: "asc" },
    });
  const filteredMedia = useMemo(() => {
    if (!trimmed) return libraryMedia;
    const q = trimmed.toLowerCase();
    return (libraryMedia as Attachment[]).filter((att) =>
      attachmentTitle(att, att.id).toLowerCase().includes(q),
    );
  }, [libraryMedia, trimmed]);

  const isLoading =
    tab === "artwork" ? loadingArt : tab === "image" ? loadingMedia : loadingMp;
  const results =
    tab === "artwork"
      ? artworks.map((a) => ({
          item: { type: "artwork" as const, id: a.id },
          title: a.oeuvresDescriptionTitre || a.id,
          url: artworkThumb(a),
        }))
      : tab === "image"
        ? (filteredMedia as Attachment[]).map((att) => ({
            item: { type: "image" as const, id: att.id },
            title: attachmentTitle(att, att.id),
            url: attachmentThumb(att),
          }))
        : multiples.map((m) => ({
            item: { type: "multipleprint" as const, id: m.id },
            title: m.estampesMultiplesDescriptionTitre || m.id,
            url: m.url || undefined,
          }));

  return (
    <div className="flex min-h-0 flex-col">
      {/* -mx-4 : full-bleed sous le séparateur ; top-0 OK car le host est pt-0. */}
      <div className="sticky top-0 z-10 -mx-4 border-b border-slate-100 bg-white px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="inline-flex min-w-0 flex-1 flex-wrap gap-1 rounded-lg bg-slate-100 p-1">
            <TabButton active={tab === "artwork"} onClick={() => setTab("artwork")}>
              {cmsNew("cms.new.pickers.selection.tabArtworks", "Œuvres")}
            </TabButton>
            <TabButton
              active={tab === "multipleprint"}
              onClick={() => setTab("multipleprint")}
            >
              {cmsNew("cms.new.pickers.selection.tabMultiples", "Multiples & estampes")}
            </TabButton>
            <TabButton active={tab === "image"} onClick={() => setTab("image")}>
              {cmsNew(
                "cms.new.pickers.selection.tabMediaLibrary",
                "Image de la médiathèque",
              )}
            </TabButton>
          </div>
          <Button
            type="button"
            size="sm"
            className="shrink-0"
            onClick={() => closePuckDrawer()}
          >
            {cmsPhrase(cmsNew, "galleryDone")}
          </Button>
        </div>
      </div>

      <div className="space-y-3 pt-4">
        <SearchBar
          search={search}
          onChangeSearch={setSearch}
          placeholder={cmsNew("cms.new.auto.placeholder.rechercher_par_titre", "Rechercher par titre…")}
        />
        {isLoading ? (
          <p className="text-sm text-slate-500">{cmsNew("cms.new.auto.text.chargement", "Chargement…")}</p>
        ) : results.length === 0 ? (
          <p className="text-sm text-slate-500">
            {trimmed
              ? cmsPhrase(cmsNew, "pickerNoResults")
              : cmsPhrase(cmsNew, "pickerNoItems")}
          </p>
        ) : (
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
            {results.map(({ item, title, url }) => {
              const key = KEY(item);
              const isSel = selected.has(key);
              const orderIndex = selectionOrder.get(key);
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => onToggle(item)}
                  className={`group relative flex flex-col items-center gap-1 rounded border p-1 ${
                    isSel
                      ? "border-primary ring-2 ring-primary/40"
                      : "border-transparent hover:border-slate-300"
                  }`}
                  title={title}
                >
                  <Thumb url={url} alt={title} size={96} />
                  <span className="line-clamp-1 w-full text-center text-[11px] text-slate-500">
                    {title}
                  </span>
                  {isSel && orderIndex != null ? (
                    <span className="absolute right-1 top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-blue-600 px-1 text-[11px] font-semibold leading-none text-white shadow-sm">
                      {orderIndex}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/** Drawer d'édition d'un item sélectionné : infos + légende RichText. */
function SelectionItemEditBody({
  title,
  url,
  typeLabel,
  initialCaption,
  onSaveCaption,
}: {
  title: string;
  url?: string;
  typeLabel: string;
  initialCaption: string;
  onSaveCaption: (caption: string) => void;
}) {
  const cmsNew = useCmsNew();
  const [caption, setCaption] = useState(initialCaption);

  return (
    <div className="flex flex-col gap-5 pt-4">
      <div className="flex items-start gap-3">
        <Thumb url={url} alt={title} size={96} />
        <div className="min-w-0 flex-1 space-y-1 pt-1">
          <p className="text-sm font-medium text-slate-900">{title}</p>
          <span className="inline-block rounded bg-slate-100 px-1.5 py-0.5 text-[10px] uppercase text-slate-500">
            {typeLabel}
          </span>
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-xs font-medium text-slate-600">
          {cmsNew(
            "cms.new.pickers.selection.captionLabel",
            "Légende (champ personnalisé)",
          )}
        </label>
        <PuckRichTextEditor
          value={caption}
          onChange={setCaption}
          placeholder={cmsNew(
            "cms.new.pickers.selection.captionPlaceholder",
            "Saisir la légende…",
          )}
        />
      </div>

      <Button
        type="button"
        className="w-full"
        onClick={() => {
          onSaveCaption(isEmptyCaptionHtml(caption) ? "" : caption);
          closePuckDrawer();
        }}
      >
        {cmsNew("cms.new.common.save", "Enregistrer")}
      </Button>
    </div>
  );
}

export function SelectionPickerField({
  value,
  onChange,
}: {
  value: SelectionItem[] | undefined;
  onChange: (value: SelectionItem[]) => void;
}) {
  const cmsNew = useCmsNew();
  const items = useMemo(() => (Array.isArray(value) ? value : []), [value]);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const artworkIds = items.filter((i) => i.type === "artwork").map((i) => i.id);
  const { instanceData } = useAuth();
  const cmsId = instanceData?.CMS?.id;

  // Résout titres/vignettes des items sélectionnés (œuvres par ids, multiples via
  // une page large mappée par id, médias via la bibliothèque du CMS — suffisant
  // pour l'aperçu éditeur).
  const { data: selArtworks = [] } = useGetTinyArtworks({
    where: artworkIds.length
      ? { id: { in: artworkIds } }
      : { id: { in: ["__none__"] } },
    take: artworkIds.length || 1,
  });
  const { data: mpPage } = useGetAllMultipleprintsCoreInfo({
    page: 0,
    pageSize: 200,
  });
  const { data: libraryMedia = [] } = useGetAllAttachments({
    where: cmsId ? { docId: cmsId } : { docId: "__none__" },
    order: { order: "asc" },
  });
  const artById = useMemo(() => {
    const m = new Map<string, TinyArtwork>();
    for (const a of selArtworks) m.set(a.id, a);
    return m;
  }, [selArtworks]);
  const mpById = useMemo(() => {
    const m = new Map<string, MultipleprintCoreInfo>();
    for (const mp of mpPage?.data ?? []) m.set(mp.id, mp);
    return m;
  }, [mpPage]);
  const mediaById = useMemo(() => {
    const m = new Map<string, Attachment>();
    for (const att of libraryMedia as Attachment[]) m.set(att.id, att);
    return m;
  }, [libraryMedia]);

  const remove = (item: SelectionItem) =>
    onChange(items.filter((i) => KEY(i) !== KEY(item)));

  // Légende custom par item (traduisible — affichée par la bascule « Champ
  // personnalisé »). Vide → retirée de l'item (rien d'extrait au save).
  const setCaption = (item: SelectionItem, caption: string) =>
    onChange(
      itemsRef.current.map((i) => {
        if (KEY(i) !== KEY(item)) return i;
        const { caption: _old, ...rest } = i;
        return caption ? { ...rest, caption } : rest;
      }),
    );

  // DnD natif (pas @dnd-kit) : la sidebar Puck a déjà un DndContext — imbriquer
  // Sortable provoque des conflits de drag sur le canva.
  const dragKeyRef = useRef<string | null>(null);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);
  const [draggingKey, setDraggingKey] = useState<string | null>(null);

  const reorderByKeys = (fromKey: string, toKey: string) => {
    if (fromKey === toKey) return;
    const list = [...itemsRef.current];
    const from = list.findIndex((i) => KEY(i) === fromKey);
    const to = list.findIndex((i) => KEY(i) === toKey);
    if (from < 0 || to < 0) return;
    const [moved] = list.splice(from, 1);
    list.splice(to, 0, moved);
    onChange(list);
  };

  const onChipDragStart = (
    e: DragEvent,
    key: string,
    card: HTMLElement | null,
  ) => {
    dragKeyRef.current = key;
    setDraggingKey(key);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", key);
    // Aperçu = toute la carte (sinon le navigateur n'affiche que le handle).
    if (card) {
      const rect = card.getBoundingClientRect();
      e.dataTransfer.setDragImage(
        card,
        Math.max(0, e.clientX - rect.left),
        Math.max(0, e.clientY - rect.top),
      );
    }
  };

  const onChipDragOver = (e: DragEvent, key: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverKey !== key) setDragOverKey(key);
  };

  const onChipDrop = (e: DragEvent, key: string) => {
    e.preventDefault();
    const from = dragKeyRef.current ?? e.dataTransfer.getData("text/plain");
    dragKeyRef.current = null;
    setDragOverKey(null);
    setDraggingKey(null);
    if (from) reorderByKeys(from, key);
  };

  const onChipDragEnd = () => {
    dragKeyRef.current = null;
    setDragOverKey(null);
    setDraggingKey(null);
  };

  const chipFor = (it: SelectionItem) => {
    if (it.type === "artwork") {
      const a = artById.get(it.id);
      return {
        title: a?.oeuvresDescriptionTitre || it.id,
        url: artworkThumb(a),
      };
    }
    if (it.type === "image") {
      const att = mediaById.get(it.id);
      return { title: attachmentTitle(att, it.id), url: attachmentThumb(att) };
    }
    const m = mpById.get(it.id);
    return {
      title:
        m?.estampesMultiplesDescriptionTitre ||
        `${cmsNew("cms.new.pickers.selection.multipleFallback", "Multiple")} ${it.id.slice(0, 6)}`,
      url: m?.url || undefined,
    };
  };

  const TYPE_LABEL: Record<string, string> = {
    artwork: cmsNew("cms.new.pickers.selection.typeArtwork", "œuvre"),
    multipleprint: cmsNew("cms.new.pickers.selection.typeMultiple", "multiple"),
    image: cmsNew("cms.new.pickers.selection.typeImage", "média"),
  };

  const openItemEdit = (it: SelectionItem) => {
    const { title, url } = chipFor(it);
    openPuckDrawer({
      title: cmsNew(
        "cms.new.pickers.selection.editItemTitle",
        "Modifier l'élément",
      ),
      maxWidth: "sm:max-w-md",
      node: (
        <SelectionItemEditBody
          title={title}
          url={url}
          typeLabel={TYPE_LABEL[it.type] ?? it.type}
          initialCaption={it.caption ?? ""}
          onSaveCaption={(caption) => setCaption(it, caption)}
        />
      ),
    });
  };

  return (
    <div className="space-y-2 p-1">
      <div className="text-xs font-medium text-slate-600">{cmsNew("cms.new.auto.text.selection", "Sélection")}</div>
      {items.length === 0 ? (
        <p className="text-xs text-slate-400">{cmsNew("cms.new.auto.text.aucun_element_selectionne", "Aucun élément sélectionné.")}</p>
      ) : (
        <div className="flex flex-col gap-2">
          {items.map((it) => {
            const key = KEY(it);
            const { title, url } = chipFor(it);
            const isDragOver = dragOverKey === key && draggingKey !== key;
            const isDragging = draggingKey === key;
            return (
              <div
                key={key}
                data-selection-chip={key}
                onDragOver={(e) => onChipDragOver(e, key)}
                onDrop={(e) => onChipDrop(e, key)}
                onDragLeave={() => {
                  if (dragOverKey === key) setDragOverKey(null);
                }}
                className={cn(
                  "flex items-center gap-1.5 rounded border bg-slate-50 py-1 pl-0.5 pr-2",
                  isDragOver
                    ? "border-blue-400 ring-2 ring-blue-200"
                    : "border-slate-200",
                  isDragging && "opacity-40",
                )}
              >
                <button
                  type="button"
                  draggable
                  onDragStart={(e) => {
                    e.stopPropagation();
                    const card = (e.currentTarget as HTMLElement).closest(
                      "[data-selection-chip]",
                    ) as HTMLElement | null;
                    onChipDragStart(e, key, card);
                  }}
                  onDragEnd={onChipDragEnd}
                  className="cursor-grab touch-none rounded p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-600 active:cursor-grabbing"
                  title={cmsNew(
                    "cms.new.pickers.selection.reorder",
                    "Réorganiser",
                  )}
                  aria-label={cmsNew(
                    "cms.new.pickers.selection.reorder",
                    "Réorganiser",
                  )}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <GripVertical size={14} />
                </button>
                <Thumb url={url} alt={title} size={40} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="max-w-[120px] truncate text-xs text-slate-600">
                      {title}
                    </span>
                    <span className="rounded bg-slate-200 px-1 text-[9px] uppercase text-slate-500">
                      {TYPE_LABEL[it.type] ?? it.type}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => openItemEdit(it)}
                  className="rounded p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-600"
                  title={cmsNew("cms.new.auto.title.modifier", "Modifier")}
                  aria-label={cmsNew(
                    "cms.new.auto.label.modifier_cet_element",
                    "Modifier cet élément",
                  )}
                >
                  <Pencil size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => remove(it)}
                  className="rounded p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-600"
                  title={cmsNew("cms.new.auto.title.retirer", "Retirer")}
                  aria-label={cmsNew("cms.new.auto.label.retirer_cet_element", "Retirer cet élément")}
                >
                  <XIcon size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() =>
          openPuckDrawer({
            title: cmsNew("cms.new.auto.title.choisir_des_uvres_multiples_et_images", "Choisir des œuvres, multiples et images"),
            maxWidth: "sm:max-w-2xl",
            node: (
              <SelectionPickerBody initialItems={items} onChange={onChange} />
            ),
          })
        }
      >
        {items.length
          ? cmsPhrase(cmsNew, "selectionEdit")
          : cmsPhrase(cmsNew, "selectionChoose")}
      </Button>
    </div>
  );
}
